/**
 * Per-user merch option lists (category, type, size, For, seller).
 * Local first; Firestore users/{uid}/prefs/merchOptions is a backup.
 */
import { getAuth } from 'firebase/auth';
import { doc, getDoc, setDoc, getFirestore } from 'firebase/firestore';
import {
  coerceMerchUserOptions,
  defaultMerchUserOptions,
  mergeTourCustomsIntoUserOptions,
  rewriteMerchPayload,
} from './calendarSettlement';
import { loadStoredJsonIfPresent, saveStoredJson, storeKey, scopedStorePrefix } from './calendarLocalStore';

export function merchUserOptionsKey(uid) {
  return `vrc.mtc.merchOptions.${String(uid || 'local')}`;
}

function canUseStorage() {
  try {
    return typeof localStorage !== 'undefined' && localStorage;
  } catch {
    return false;
  }
}

export function loadMerchUserOptionsLocal(uid) {
  if (!canUseStorage()) return null;
  try {
    const raw = localStorage.getItem(merchUserOptionsKey(uid));
    if (!raw) return null;
    return coerceMerchUserOptions(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveMerchUserOptionsLocal(uid, options) {
  if (!canUseStorage()) return;
  try {
    localStorage.setItem(merchUserOptionsKey(uid), JSON.stringify(coerceMerchUserOptions(options)));
  } catch {
    /* quota / private mode */
  }
}

export async function loadMerchUserOptionsWithSource(uid) {
  const local = loadMerchUserOptionsLocal(uid);
  if (local) return { options: local, source: 'local' };
  if (uid) {
    try {
      const snap = await getDoc(doc(getFirestore(), 'users', uid, 'prefs', 'merchOptions'));
      if (snap.exists()) {
        const remote = coerceMerchUserOptions(snap.data());
        saveMerchUserOptionsLocal(uid, remote);
        return { options: remote, source: 'remote' };
      }
    } catch {
      /* rules / offline */
    }
  }
  return { options: defaultMerchUserOptions(), source: 'default' };
}

export async function loadMerchUserOptions(uid) {
  return (await loadMerchUserOptionsWithSource(uid)).options;
}

export async function persistMerchUserOptions(uid, options) {
  const next = coerceMerchUserOptions(options);
  saveMerchUserOptionsLocal(uid, next);
  if (!uid) return next;
  try {
    await setDoc(doc(getFirestore(), 'users', uid, 'prefs', 'merchOptions'), next, { merge: true });
  } catch {
    /* local still saved */
  }
  return next;
}

export function currentMerchUserId() {
  try {
    return getAuth().currentUser?.uid || '';
  } catch {
    return '';
  }
}

export function hydrateMerchUserOptions(uid, sheet) {
  const local = loadMerchUserOptionsLocal(uid) || defaultMerchUserOptions();
  return mergeTourCustomsIntoUserOptions(local, sheet);
}

/** Rewrite every locally cached merch night after a rename/delete. */
export function rewriteAllLocalMerchSheets(change) {
  if (!canUseStorage()) return;
  const prefix = 'vrc.mtc.merch.';
  const ownPrefix = scopedStorePrefix('merch');
  const keys = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(prefix) || key.startsWith('vrc.mtc.merchOptions.')) continue;
    // Only this user's sheets (and not-yet-claimed old keys); never another account's on a shared browser.
    if (key.startsWith(ownPrefix) || !key.startsWith(`${prefix}@`)) keys.push(key);
  }
  keys.forEach((key) => {
    try {
      const raw = JSON.parse(localStorage.getItem(key) || 'null');
      if (!raw) return;
      localStorage.setItem(key, JSON.stringify(rewriteMerchPayload(raw, change)));
    } catch {
      /* skip bad keys */
    }
  });
}

export { storeKey, loadStoredJsonIfPresent, saveStoredJson };
